import { useState } from "react";
import { Maximize2, Navigation } from "lucide-react";
import type { PointOfInterest, RankedSpace } from "../../ai-mode/types";
import { useProposalT } from "../i18n/ProposalI18nContext";
import { POI_CATEGORIES } from "../../ai-mode/constants";
import { SpacesLocationMap, POI_COLORS, isMappable } from "../../map";
import { spaceService } from "@/services/space-service";
import { haversineDistance, getSpaceDisplayName } from "../utils";
import {
  SearchAutocomplete,
  type SearchAutocompleteHandle,
} from "@/components/inputs/search-autocomplete/search-autocomplete";
import { useSearchSpaces } from "@/hooks/useSearchSpaces";
import { useRef } from "react";

interface MapTabProps {
  spaces: RankedSpace[];
  mapCenter: { lat: number; lng: number };
  filteredPOIs: PointOfInterest[];
  selectedSpaceId: string;
  selectedPOICategories: string[];
  onTogglePOI: (category: string) => void;
  onSelectSpace: (id: string) => void;
  onFullscreen: () => void;
}

export function MapTab({
  spaces,
  mapCenter,
  filteredPOIs,
  selectedSpaceId,
  selectedPOICategories,
  onTogglePOI,
  onSelectSpace,
  onFullscreen,
}: MapTabProps) {
  const { t } = useProposalT();
  const [refInput, setRefInput] = useState("");
  const [refCoords, setRefCoords] = useState<{
    lat: number;
    lng: number;
    label: string;
  } | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const autocompleteRef = useRef<SearchAutocompleteHandle>(null);

  const {
    spaces: searchResults,
    handleSearchSpaces,
    isLoading: isSearching,
  } = useSearchSpaces();

  async function resolveLocation(label: string) {
    if (!label.trim()) return;
    setIsResolving(true);
    try {
      const response = await spaceService.getSpaceByLocation(label);
      const result = Array.isArray(response) ? response[0] : response;
      if (result?.lat != null && result?.lng != null) {
        setRefCoords({
          lat: Number(result.lat),
          lng: Number(result.lng),
          label,
        });
      }
    } catch {
      // silently ignore
    } finally {
      setIsResolving(false);
    }
  }

  const selectedSpace = spaces.find((s) => s.id === selectedSpaceId);
  // Warn when the selected option can't be placed, but only while the map is
  // mounted — with zero mappable spaces the full no-address fallback already
  // covers it (e.g. the drawer's single-room map).
  const showSelectedNoAddress =
    selectedSpace != null &&
    !isMappable(selectedSpace) &&
    spaces.some(isMappable);

  // Ungeocoded spaces would silently measure the distance to (0,0) — keep
  // them out, but number the rest by their original position in the list.
  const distances =
    refCoords != null
      ? spaces
          .map((s, i) => ({ space: s, index: i }))
          .filter(({ space }) => isMappable(space))
          .map(({ space: s, index: i }) => ({
            id: s.id,
            name: getSpaceDisplayName(
              s,
              i,
              t("spaces.option", { number: i + 1 }),
            ),
            distanceKm: haversineDistance(
              refCoords.lat,
              refCoords.lng,
              s.lat,
              s.lng,
            ),
          }))
          .sort((a, b) => a.distanceKm - b.distanceKm)
      : null;

  return (
    <div role="tabpanel">
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <button
          style={{
            padding: "6px 12px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 500,
            background: "#FFD900",
            color: "#1A1814",
            border: "1px solid #FFD900",
            cursor: "default",
          }}
        >
          {t("map.coworkings")}
        </button>
        {POI_CATEGORIES.map((category) => {
          const color = POI_COLORS[category.value];
          const isSelected = selectedPOICategories.includes(category.value);
          return (
            <button
              key={category.value}
              onClick={() => onTogglePOI(category.value)}
              className="transition-all"
              style={{
                padding: "6px 12px",
                borderRadius: "999px",
                fontSize: "12px",
                fontWeight: 500,
                cursor: "pointer",
                background: isSelected ? color : "white",
                color: isSelected ? "white" : "#4b5563",
                border: `1px solid ${isSelected ? color : "#e5e7eb"}`,
              }}
            >
              {t(`poi.${category.value}`)}
            </button>
          );
        })}
      </div>

      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          height: "440px",
          background: "#f3f4f6",
          border: "1px solid #E8E6E3",
        }}
      >
        <SpacesLocationMap
          center={mapCenter}
          zoom={15}
          spaces={spaces}
          pois={filteredPOIs}
          highlightedId={selectedSpaceId}
          onSpaceClick={onSelectSpace}
          labels={{
            noAddress: t("map.addressNotProvided"),
            recenter: t("map.recenter"),
            recenterTitle: t("map.recenterTitle"),
            poiNoDescription: t("poi.noDescription"),
          }}
          getDisplayName={(space) => {
            const idx = spaces.findIndex((s) => s.id === space.id);
            const i = idx >= 0 ? idx : 0;
            return getSpaceDisplayName(
              space,
              i,
              t("spaces.option", { number: i + 1 }),
            );
          }}
        />
        {showSelectedNoAddress && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              padding: "6px 12px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 500,
              background: "white",
              color: "#78736D",
              border: "1px solid #E8E6E3",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            {t("map.selectedNoAddress")}
          </div>
        )}
        <button
          onClick={onFullscreen}
          aria-label={t("map.fullscreenAriaLabel")}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            padding: "6px 12px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 500,
            background: "white",
            color: "#1A1814",
            border: "1px solid #E8E6E3",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <Maximize2 size={14} />
          {t("map.fullscreen")}
        </button>
      </div>

      {/* Distance calculator */}
      <div
        className="mt-4 rounded-xl p-4"
        style={{ border: "1px solid #E8E6E3", background: "#FDFBF7" }}
      >
        <p className="text-xs font-semibold mb-2" style={{ color: "#78736D" }}>
          {t("map.calculateDistance")}
        </p>
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <SearchAutocomplete
              ref={autocompleteRef}
              placeholder={t("map.placeholder")}
              className="!mt-0"
              listClassName="!absolute !left-0 !w-full !top-auto"
              options={searchResults.map((s) => {
                const fullLabel =
                  `${s.name ?? ""}${s.address ? ` - ${s.address}` : ""}`.trim() ||
                  s.name ||
                  s.address;
                return {
                  value: s.placeId,
                  label: fullLabel,
                  description: s.address,
                  data: s,
                };
              })}
              value={refInput}
              isLoading={isSearching}
              onInputChange={(val) => {
                setRefInput(val);
                handleSearchSpaces(val);
              }}
              onValueChange={(_, data) => {
                if (data) {
                  const label =
                    `${data.name ?? ""}${data.address ? ` - ${data.address}` : ""}`.trim() ||
                    data.name ||
                    data.address;
                  setRefInput(label);
                  void resolveLocation(label);
                }
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => void resolveLocation(refInput)}
            disabled={isResolving || !refInput.trim()}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              background: "#1A1814",
              color: "white",
              border: "none",
              cursor: isResolving || !refInput.trim() ? "default" : "pointer",
              opacity: isResolving || !refInput.trim() ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexShrink: 0,
              height: "36px",
            }}
          >
            <Navigation size={13} />
            {isResolving ? t("map.searching") : t("map.calculate")}
          </button>
        </div>

        {distances != null && (
          <div className="mt-3">
            <p className="text-xs mb-2" style={{ color: "#9E9A94" }}>
              {t("map.distancesFrom")}{" "}
              <span style={{ fontWeight: 600, color: "#43403D" }}>
                {refCoords?.label}
              </span>
            </p>
            <div className="space-y-1.5">
              {distances.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between text-xs"
                  style={{ color: "#43403D" }}
                >
                  <span className="truncate mr-3">{d.name}</span>
                  <span
                    className="flex-shrink-0 font-semibold px-2 py-0.5 rounded-md"
                    style={{ background: "#F0EEEB", color: "#1A1814" }}
                  >
                    {d.distanceKm < 1
                      ? `${Math.round(d.distanceKm * 1000)} m`
                      : `${d.distanceKm.toFixed(1)} km`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
